/* ==========================================================================
   ABS DEVELOPERS — LUXURY INTERACTION SYSTEM
   File: assets/abs-luxury.js
   Purpose: Global interaction and motion foundation for the ABS Shopify theme.
   ========================================================================== */

(() => {
  'use strict';

  const ABS = {
    selectors: {
      reveal: '[data-abs-reveal]',
      header: '[data-abs-header]',
      headerToggle: '[data-abs-menu-toggle]',
      headerMenu: '[data-abs-menu]',
      headerClose: '[data-abs-menu-close]',
      headerOverlay: '[data-abs-menu-overlay]',
      parallax: '[data-abs-parallax]',
      counter: '[data-abs-counter]',
      magnetic: '[data-abs-magnetic]',
      scrollLink: '[data-abs-scroll-link]',
      video: '[data-abs-video]',
      videoToggle: '[data-abs-video-toggle]',
      accordion: '[data-abs-accordion]',
      accordionButton: '[data-abs-accordion-button]',
      cursorTarget: '[data-abs-cursor-target]'
    },

    classes: {
      visible: 'is-visible',
      scrolled: 'is-scrolled',
      menuOpen: 'is-menu-open',
      open: 'is-open',
      ready: 'is-abs-ready',
      paused: 'is-paused'
    },

    state: {
      lastScrollY: 0,
      ticking: false,
      revealObserver: null,
      counterObserver: null,
      reducedMotion: false
    }
  };

  /* ------------------------------------------------------------------------
     UTILITIES
     ------------------------------------------------------------------------ */

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isShopifyEditor = () =>
    document.documentElement.classList.contains('shopify-design-mode');

  const setBodyScrollLock = (locked) => {
    if (locked) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.dataset.absScrollY = String(window.scrollY);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';

      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      const scrollY = Number(document.body.dataset.absScrollY || 0);

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      delete document.body.dataset.absScrollY;

      window.scrollTo(0, scrollY);
    }
  };

  const debounce = (fn, delay = 150) => {
    let timer;

    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), delay);
    };
  };

  /* ------------------------------------------------------------------------
     PAGE READY
     ------------------------------------------------------------------------ */

  const markPageReady = () => {
    window.requestAnimationFrame(() => {
      document.documentElement.classList.add(ABS.classes.ready);
    });
  };

  /* ------------------------------------------------------------------------
     REVEAL ON SCROLL
     ------------------------------------------------------------------------ */

  const destroyRevealObserver = () => {
    if (ABS.state.revealObserver) {
      ABS.state.revealObserver.disconnect();
      ABS.state.revealObserver = null;
    }
  };

  const initRevealAnimations = (scope = document) => {
    const items = qsa(ABS.selectors.reveal, scope);

    if (!items.length) return;

    if (ABS.state.reducedMotion || isShopifyEditor() || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add(ABS.classes.visible));
      return;
    }

    if (!ABS.state.revealObserver) {
      ABS.state.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add(ABS.classes.visible);
            observer.unobserve(entry.target);
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -8% 0px',
          threshold: 0.08
        }
      );
    }

    items.forEach((item) => {
      if (item.classList.contains(ABS.classes.visible)) return;
      ABS.state.revealObserver.observe(item);
    });
  };

  /* ------------------------------------------------------------------------
     HEADER SCROLL STATE
     ------------------------------------------------------------------------ */

  const updateHeaderState = () => {
    const header = qs(ABS.selectors.header);

    if (!header) return;

    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const threshold = Number(header.dataset.absScrollThreshold || 24);

    header.classList.toggle(ABS.classes.scrolled, scrollY > threshold);
    header.dataset.absScrollDirection =
      scrollY > ABS.state.lastScrollY && scrollY > threshold ? 'down' : 'up';

    ABS.state.lastScrollY = Math.max(scrollY, 0);
  };

  const onScroll = () => {
    if (ABS.state.ticking) return;

    ABS.state.ticking = true;

    window.requestAnimationFrame(() => {
      updateHeaderState();
      updateParallax();
      ABS.state.ticking = false;
    });
  };

  /* ------------------------------------------------------------------------
     MOBILE MENU
     ------------------------------------------------------------------------ */

  const closeMenu = (header) => {
    if (!header || !header.classList.contains(ABS.classes.menuOpen)) return;

    const toggle = qs(ABS.selectors.headerToggle, header);
    const menu = qs(ABS.selectors.headerMenu, header);

    header.classList.remove(ABS.classes.menuOpen);
    document.documentElement.classList.remove(ABS.classes.menuOpen);

    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', toggle.dataset.labelOpen || 'Open menu');
    }

    if (menu) {
      menu.setAttribute('aria-hidden', 'true');
    }

    setBodyScrollLock(false);
  };

  const openMenu = (header) => {
    if (!header || header.classList.contains(ABS.classes.menuOpen)) return;

    const toggle = qs(ABS.selectors.headerToggle, header);
    const menu = qs(ABS.selectors.headerMenu, header);

    header.classList.add(ABS.classes.menuOpen);
    document.documentElement.classList.add(ABS.classes.menuOpen);

    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', toggle.dataset.labelClose || 'Close menu');
    }

    if (menu) {
      menu.setAttribute('aria-hidden', 'false');
    }

    setBodyScrollLock(true);

    const firstFocusable = qs(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      menu || header
    );

    if (firstFocusable) {
      window.setTimeout(() => firstFocusable.focus(), 80);
    }
  };

  const initHeaderMenu = (scope = document) => {
    qsa(ABS.selectors.header, scope).forEach((header) => {
      if (header.dataset.absMenuBound === 'true') return;

      const toggle = qs(ABS.selectors.headerToggle, header);
      const closeButtons = qsa(
        `${ABS.selectors.headerClose}, ${ABS.selectors.headerOverlay}`,
        header
      );
      const menu = qs(ABS.selectors.headerMenu, header);

      if (!toggle || !menu) return;

      header.dataset.absMenuBound = 'true';

      toggle.addEventListener('click', () => {
        const isOpen = header.classList.contains(ABS.classes.menuOpen);

        if (isOpen) {
          closeMenu(header);
        } else {
          openMenu(header);
        }
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => closeMenu(header));
      });

      qsa('a[href]', menu).forEach((link) => {
        link.addEventListener('click', () => closeMenu(header));
      });

      header.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeMenu(header);
          toggle.focus();
        }
      });
    });
  };

  /* ------------------------------------------------------------------------
     SMOOTH ANCHOR LINKS
     ------------------------------------------------------------------------ */

  const initSmoothLinks = (scope = document) => {
    qsa(ABS.selectors.scrollLink, scope).forEach((link) => {
      if (link.dataset.absScrollBound === 'true') return;

      link.dataset.absScrollBound = 'true';

      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');

        if (!href || !href.startsWith('#') || href === '#') return;

        const target = qs(href);

        if (!target) return;

        event.preventDefault();

        const header = qs(ABS.selectors.header);
        const headerOffset = header ? header.offsetHeight : 0;
        const top =
          target.getBoundingClientRect().top +
          window.pageYOffset -
          headerOffset;

        window.scrollTo({
          top,
          behavior: ABS.state.reducedMotion ? 'auto' : 'smooth'
        });
      });
    });
  };

  /* ------------------------------------------------------------------------
     SUBTLE PARALLAX
     ------------------------------------------------------------------------ */

  const updateParallax = () => {
    if (ABS.state.reducedMotion || window.innerWidth < 750) return;

    const items = qsa(ABS.selectors.parallax);

    items.forEach((item) => {
      const rect = item.getBoundingClientRect();

      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      const speed = Number(item.dataset.absParallax || 0.08);
      const centerOffset =
        rect.top + rect.height / 2 - window.innerHeight / 2;
      const movement = clamp(centerOffset * speed * -1, -80, 80);

      item.style.transform = `translate3d(0, ${movement}px, 0)`;
    });
  };

  /* ------------------------------------------------------------------------
     NUMBER COUNTERS
     ------------------------------------------------------------------------ */

  const animateCounter = (element) => {
    if (element.dataset.absCounted === 'true') return;

    element.dataset.absCounted = 'true';

    const rawTarget = element.dataset.absCounter || element.textContent || '0';
    const target = Number(rawTarget.replace(/[^0-9.-]/g, ''));

    if (!Number.isFinite(target)) return;

    const prefix = element.dataset.absCounterPrefix || '';
    const suffix = element.dataset.absCounterSuffix || '';
    const decimals = Number(element.dataset.absCounterDecimals || 0);
    const duration = Number(element.dataset.absCounterDuration || 1300);

    if (ABS.state.reducedMotion) {
      element.textContent = `${prefix}${target.toFixed(decimals)}${suffix}`;
      return;
    }

    const start = performance.now();

    const frame = (time) => {
      const progress = clamp((time - start) / duration, 0, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const value = target * eased;

      element.textContent = `${prefix}${value.toFixed(decimals)}${suffix}`;

      if (progress < 1) {
        window.requestAnimationFrame(frame);
      } else {
        element.textContent = `${prefix}${target.toFixed(decimals)}${suffix}`;
      }
    };

    window.requestAnimationFrame(frame);
  };

  const initCounters = (scope = document) => {
    const counters = qsa(ABS.selectors.counter, scope);

    if (!counters.length) return;

    if (ABS.state.reducedMotion || !('IntersectionObserver' in window)) {
      counters.forEach(animateCounter);
      return;
    }

    if (!ABS.state.counterObserver) {
      ABS.state.counterObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            animateCounter(entry.target);
            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.35
        }
      );
    }

    counters.forEach((counter) => {
      if (counter.dataset.absCounted !== 'true') {
        ABS.state.counterObserver.observe(counter);
      }
    });
  };

  /* ------------------------------------------------------------------------
     MAGNETIC BUTTON EFFECT
     ------------------------------------------------------------------------ */

  const initMagneticElements = (scope = document) => {
    if (ABS.state.reducedMotion || window.matchMedia('(pointer: coarse)').matches) return;

    qsa(ABS.selectors.magnetic, scope).forEach((element) => {
      if (element.dataset.absMagneticBound === 'true') return;

      element.dataset.absMagneticBound = 'true';

      element.addEventListener('mousemove', (event) => {
        const rect = element.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;

        element.style.transform = `translate3d(${x * 0.08}px, ${y * 0.08}px, 0)`;
      });

      element.addEventListener('mouseleave', () => {
        element.style.transform = '';
      });
    });
  };

  /* ------------------------------------------------------------------------
     VIDEO CONTROLS
     ------------------------------------------------------------------------ */

  const syncVideoButton = (video, button) => {
    if (!video || !button) return;

    const isPaused = video.paused;

    button.classList.toggle(ABS.classes.paused, isPaused);
    button.setAttribute('aria-pressed', String(!isPaused));

    const playLabel = button.dataset.labelPlay || 'Play video';
    const pauseLabel = button.dataset.labelPause || 'Pause video';

    button.setAttribute('aria-label', isPaused ? playLabel : pauseLabel);
  };

  const initVideos = (scope = document) => {
    qsa(ABS.selectors.video, scope).forEach((wrapper) => {
      if (wrapper.dataset.absVideoBound === 'true') return;

      const video = qs('video', wrapper);
      const button = qs(ABS.selectors.videoToggle, wrapper);

      if (!video || !button) return;

      wrapper.dataset.absVideoBound = 'true';

      if (ABS.state.reducedMotion) {
        video.pause();
        video.removeAttribute('autoplay');
      }

      syncVideoButton(video, button);

      button.addEventListener('click', async () => {
        try {
          if (video.paused) {
            await video.play();
          } else {
            video.pause();
          }
        } catch (error) {
          console.warn('ABS video interaction:', error);
        }

        syncVideoButton(video, button);
      });

      video.addEventListener('play', () => syncVideoButton(video, button));
      video.addEventListener('pause', () => syncVideoButton(video, button));
      video.addEventListener('ended', () => syncVideoButton(video, button));
    });
  };

  /* ------------------------------------------------------------------------
     ACCORDIONS
     ------------------------------------------------------------------------ */

  const initAccordions = (scope = document) => {
    qsa(ABS.selectors.accordion, scope).forEach((accordion) => {
      if (accordion.dataset.absAccordionBound === 'true') return;

      const button = qs(ABS.selectors.accordionButton, accordion);
      const panelId = button?.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;

      if (!button || !panel) return;

      accordion.dataset.absAccordionBound = 'true';

      const setState = (open) => {
        accordion.classList.toggle(ABS.classes.open, open);
        button.setAttribute('aria-expanded', String(open));
        panel.hidden = !open;
      };

      setState(button.getAttribute('aria-expanded') === 'true');

      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') === 'true';
        setState(!open);
      });
    });
  };

  /* ------------------------------------------------------------------------
     VIEWPORT HEIGHT VARIABLE
     ------------------------------------------------------------------------ */

  const updateViewportUnit = () => {
    document.documentElement.style.setProperty(
      '--abs-vh',
      `${window.innerHeight * 0.01}px`
    );
  };

  /* ------------------------------------------------------------------------
     SHOPIFY THEME EDITOR EVENTS
     ------------------------------------------------------------------------ */

  const initSection = (scope = document) => {
    initRevealAnimations(scope);
    initHeaderMenu(scope);
    initSmoothLinks(scope);
    initCounters(scope);
    initMagneticElements(scope);
    initVideos(scope);
    initAccordions(scope);
  };

  const bindShopifyEditorEvents = () => {
    document.addEventListener('shopify:section:load', (event) => {
      initSection(event.target);
      updateHeaderState();
      updateParallax();
    });

    document.addEventListener('shopify:section:reorder', () => {
      updateHeaderState();
      updateParallax();
    });

    document.addEventListener('shopify:section:select', (event) => {
      qsa(ABS.selectors.reveal, event.target).forEach((item) => {
        item.classList.add(ABS.classes.visible);
      });
    });

    document.addEventListener('shopify:block:select', (event) => {
      const reveal =
        event.target.matches?.(ABS.selectors.reveal)
          ? event.target
          : qs(ABS.selectors.reveal, event.target);

      if (reveal) {
        reveal.classList.add(ABS.classes.visible);
      }
    });
  };

  /* ------------------------------------------------------------------------
     GLOBAL EVENTS
     ------------------------------------------------------------------------ */

  const bindGlobalEvents = () => {
    window.addEventListener('scroll', onScroll, { passive: true });

    window.addEventListener(
      'resize',
      debounce(() => {
        updateViewportUnit();
        updateHeaderState();
        updateParallax();

        if (window.innerWidth >= 990) {
          qsa(ABS.selectors.header).forEach((header) => closeMenu(header));
        }
      }, 120),
      { passive: true }
    );

    window.addEventListener('orientationchange', () => {
      window.setTimeout(() => {
        updateViewportUnit();
        updateHeaderState();
        updateParallax();
      }, 200);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      qsa(ABS.selectors.header).forEach((header) => {
        if (header.classList.contains(ABS.classes.menuOpen)) {
          closeMenu(header);
        }
      });
    });
  };

  /* ------------------------------------------------------------------------
     INITIALIZE
     ------------------------------------------------------------------------ */

  const init = () => {
    ABS.state.reducedMotion = prefersReducedMotion();

    updateViewportUnit();
    initSection(document);
    bindGlobalEvents();
    bindShopifyEditorEvents();
    updateHeaderState();
    updateParallax();
    markPageReady();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
